/**
 * API Service - Core module
 * Handles base request logic, health checks, and configuration
 */

import * as socialApi from './social.js';
import * as launchApi from './launch.js';
import * as aiApi from './ai.js';

// Determine API base URL
function getApiBase() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (hostname === 'yobitch.fun' || hostname === 'www.yobitch.fun') {
      return process.env.REACT_APP_RAILWAY_URL || 'https://cook.yobitch.fun';
    }

    if (hostname.includes('vercel.app')) {
      return process.env.REACT_APP_RAILWAY_URL || 'https://cook.yobitch.fun';
    }

    // Local development - use webpack proxy (relative URLs)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }
  }

  return 'http://localhost:3001';
}

const API_BASE = getApiBase();

class ApiError extends Error {
  constructor(message, category, status) {
    super(message);
    this.name = 'ApiError';
    this.category = category;
    this.status = status;
  }
}

class ApiService {
  constructor() {
    this.baseUrl = API_BASE;
    this.configCache = null;
    this.configCacheTime = null;
    this.backendAvailable = null;
    this.unavailableEndpoints = new Map();
  }

  async request(endpoint, options = {}) {
    const cacheEntry = this.unavailableEndpoints.get(endpoint);
    if (cacheEntry) {
      const timeSinceError = Date.now() - cacheEntry.timestamp;
      const cacheDuration = cacheEntry.status === 429 ? 60000 : 30000;
      if (timeSinceError < cacheDuration) {
        return {
          error: cacheEntry.status === 429 ? 'Rate limited' : 'Service unavailable',
          status: cacheEntry.status,
          authenticated: false,
        };
      }
    }

    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (response.status === 503 || response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const cacheDuration =
          response.status === 429 ? (retryAfter ? parseInt(retryAfter) * 1000 : 60000) : 30000;

        this.unavailableEndpoints.set(endpoint, {
          status: response.status,
          timestamp: Date.now(),
        });

        setTimeout(() => {
          this.unavailableEndpoints.delete(endpoint);
        }, cacheDuration);

        return {
          error: response.status === 429 ? 'Rate limited' : 'Service unavailable',
          status: response.status,
          authenticated: false,
          retryAfter: retryAfter ? parseInt(retryAfter) : null,
        };
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        if (!response.ok) {
          throw new ApiError('Invalid response', 'PARSE_ERROR', response.status);
        }
        throw new ApiError('Failed to parse response', 'PARSE_ERROR', response.status);
      }

      if (!response.ok) {
        throw new ApiError(data.error || 'Request failed', data.category, response.status);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new ApiError('Request timed out. Please check your connection.', 'TIMEOUT_ERROR', 0);
      }
      if (error instanceof ApiError) throw error;
      if (error.message?.includes('503') || error.message?.includes('Service Unavailable')) {
        return { error: 'Service unavailable', status: 503 };
      }
      throw new ApiError('Backend not available', 'NETWORK_ERROR', 0);
    }
  }

  async checkBackend() {
    if (this.backendAvailable !== null) return this.backendAvailable;

    try {
      await this.request('/api/health');
      this.backendAvailable = true;
    } catch {
      this.backendAvailable = false;
    }
    return this.backendAvailable;
  }

  async getPlatformConfig(forceRefresh = false) {
    const cacheAge = this.configCacheTime ? Date.now() - this.configCacheTime : Infinity;
    if (!forceRefresh && this.configCache && cacheAge < 30000) {
      return { success: true, config: this.configCache };
    }

    try {
      const data = await this.request('/api/platform/config');
      if (data.success) {
        this.configCache = data.config;
        this.configCacheTime = Date.now();
      }
      return data;
    } catch (error) {
      if (this.configCache) {
        return { success: true, config: this.configCache };
      }
      throw error;
    }
  }

  async getConfigStatus(forceRefresh = false) {
    const cacheAge = this.configCacheTime ? Date.now() - this.configCacheTime : Infinity;

    if (!forceRefresh && this.configCache && cacheAge < 60000) {
      return this.configCache;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      const data = await this.request('/api/config/status', { signal: controller.signal });
      clearTimeout(timeoutId);

      this.configCache = data.apis;
      this.configCacheTime = Date.now();
      return data.apis;
    } catch {
      return {
        twitter: { configured: false },
        reddit: { configured: false },
        news: { configured: false },
        helius: { configured: false },
        birdeye: { configured: false },
        jito: { configured: false },
        telegram: { configured: false },
        lunarcrush: { configured: false },
        pinata: { configured: false },
        discord: { configured: false },
        brave: { configured: false },
      };
    }
  }

  async healthCheck() {
    return this.request('/api/health');
  }
}

const api = new ApiService();

api.getTelegramStatus = socialApi.getTelegramStatus;
api.getTelegramTrending = socialApi.getTelegramTrending;
api.searchTelegramAlpha = socialApi.searchTelegramAlpha;
api.getTelegramMentions = socialApi.getTelegramMentions;
api.searchTwitter = socialApi.searchTwitter;
api.searchNews = socialApi.searchNews;
api.getFullArticle = socialApi.getFullArticle;
api.getTwitterTrends = socialApi.getTwitterTrends;
api.getRedditTrends = socialApi.getRedditTrends;
api.getNewsHeadlines = socialApi.getNewsHeadlines;
api.getTwitterMentions = socialApi.getTwitterMentions;
api.getLaunchReadiness = launchApi.getLaunchReadiness;
api.getAiHealth = aiApi.getAiHealth;
api.postAiRecommend = aiApi.postAiRecommend;

export { api, ApiError, ApiService };
export default api;
